import { ElementRef, Renderer2, NgZone, OnDestroy, Injectable } from "@angular/core";
import { Dialog } from "@bfx/primeng/dialog";

import { LoggerService } from "@bfx/logger";
import { first, ReplaySubject, Subject, Subscription } from "rxjs";
import { AnimationEvent } from "@angular/animations";

const logger = LoggerService.for(__dirname);

export const CLASS_DIALOG_BG: string = "dialog-bg-blur";

@Injectable()
export class NativeDesktopDialog extends Dialog {
  /**
   * NativeDesktopDialog's addOverlayElement & removeOverlayElement apply to both native and browser
   */
  static wrap(_dialog: Dialog, isNative): NativeDesktopDialog {
    if (_dialog instanceof NativeDesktopDialog) return _dialog;

    let dialog: NativeDesktopDialog = Object.setPrototypeOf(
      _dialog,
      Object.create(NativeDesktopDialog.prototype),
    );

    dialog.isNative = isNative;

    if (isNative) {
      dialog.replayShow = new ReplaySubject<void>(1);
      dialog.onShowSubscription = dialog.onShow
        .pipe(first())
        .subscribe(() => dialog.replayShow.next());
    }

    return dialog;
  }

  private bodyElement: Element;
  private isNative: boolean;
  private mouseMoveHandler: (mouseEvent: MouseEvent) => void;
  private mouseUpHandler: (mouseEvent: MouseEvent) => void;
  private onShowSubscription: Subscription;
  private overlayElement: Element;
  private replayShow: Subject<void>;
  private replayShowSubscription: Subscription;

  private constructor(el: ElementRef, renderer: Renderer2, zone: NgZone) {
    super(el, renderer, zone);
  }

  addBrowserDialog(bodyElement: Element): void {
    let elements: HTMLCollectionOf<Element> = bodyElement.getElementsByClassName("wrapper");
    this.addOverlayElement(elements);
  }

  addNativeDialog(element: ElementRef, bodyElement: Element): void {
    this.appendTo = null;

    if (bodyElement != null) {
      this.bodyElement = bodyElement;
    } else {
      this.bodyElement = element?.nativeElement?.ownerDocument?.body ?? document.body;
    }

    this.replayShowSubscription = this.replayShow.pipe(first()).subscribe(() => {
      this.container = this.container ?? element?.nativeElement?.children?.[0];
      const popoutBody: Element = this.getPopoutBody();

      if (this.container == null) {
        this.unbindDocumentDragListener();
        return;
      }

      if (this.isBodyMain(popoutBody)) {
        this.showOnMain();
      } else {
        this.showOnPopout();
      }
    });
  }

  private showOnMain() {
    let elements: HTMLCollectionOf<Element> = this.bodyElement.getElementsByClassName("wrapper");
    this.addOverlayElement(elements);
    super.positionOverlay();
  }

  private showOnPopout() {
    this.bodyElement.appendChild(this.container);

    super.positionOverlay();

    this.addMouseUpHandler();
    this.addNativeMouseMoveHandler();

    this.positionOnPopout(this.bodyElement);
    this.addOverlayElement([this.bodyElement.firstElementChild]);

    if (this.modal && this.mask == null) {
      this.enableModality();
    }

    this.addPopoutMask(this.bodyElement);
  }

  private isBodyMain(body: Element) {
    return body == null || this.bodyElement == window.document.body;
  }

  private getPopoutBody(): Element {
    return this.bodyElement.ownerDocument.getElementsByClassName("gl-popout-body")[0];
  }

  private addNativeMouseMoveHandler() {
    this.mouseMoveHandler = (mouseEvent: MouseEvent) => {
      let rect: ClientRect = this.bodyElement.getBoundingClientRect();

      this.unbindDocumentDragListener();

      this.onDrag(mouseEvent);

      if (this.container != null) {
        try {
          if (rect != null && rect.width == 0 && rect.height == 0) {
            rect = this.container.ownerDocument.body.getBoundingClientRect();
          }
        } catch (e) {
          logger.out.warn(`Failed to determine bounding rect for dialog adjustment.`);
        }

        let x: number = this.container["offsetLeft"];
        let y: number = this.container["offsetTop"];
        let width: number = this.container["offsetWidth"];
        let height: number = this.container["offsetHeight"];
        let changed: boolean = false;

        if (width == 0 || height == 0) {
          return;
        }

        if (x < 0) {
          x = 0;
          changed = true;
        }

        if (y < 0) {
          y = 0;
          changed = true;
        }

        if (x + width > rect.width) {
          x = rect.width - width;
          changed = true;
        }

        if (y + height > rect.height) {
          y = rect.height - height;
          changed = true;
        }

        if (y <= 34) {
          y = 34;
          changed = true;
        }

        if (changed) {
          if (x >= 0) {
            this.container["style"].left = x + "px";
          }

          if (y >= 0) {
            this.container["style"].top = y + "px";
          }
        }
      }
    };

    this.bodyElement.addEventListener("mousemove", this.mouseMoveHandler);
  }

  addMouseUpHandler() {
    this.mouseUpHandler = (mouseEvent: MouseEvent) => {
      this.endDrag(mouseEvent);
    };
    this.bodyElement.addEventListener("mouseup", this.mouseUpHandler);
  }

  addOverlayElement(elements: HTMLCollectionOf<Element> | Array<Element>) {
    this.removeOverlayElement();

    if (elements?.length > 0 && elements[0] != null) {
      let element = elements[0];
      element.classList.add(CLASS_DIALOG_BG);
      this.overlayElement = element;
    }
  }

  private addPopoutMask(popoutBody: Element) {
    if (this.mask != null) {
      popoutBody.appendChild(this.mask);
    }
  }

  disableModality(): void {
    if (this.mask) this.mask?.parentElement?.removeChild?.(this.mask);

    this.mask = null;
  }

  removeOverlayElement(): void {
    if (this?.overlayElement != null) {
      let element = this.overlayElement;
      element.classList.remove(CLASS_DIALOG_BG);
      this.overlayElement = null;
    }
  }

  /**
     no-op of the internal Dialog positionOverlay() called by animation logic

     use super.positionOverlay() or popoutPosition for native desktop
  */
  positionOverlay(): void {
    if (!this.isNative) super.positionOverlay();

    return;
  }

  private positionOnPopout(popoutBody: Element) {
    let rect: DOMRect = popoutBody.getBoundingClientRect();
    let width: number = this.container["offsetWidth"];
    let height: number = this.container["offsetHeight"];

    this.container["style"].left = (rect.width - width) / 2 + "px";
    this.container["style"].top = (rect.height - height) / 2 + "px";

    this.positionLeft = (rect.width - width) / 2;
    this.positionTop = (rect.height - height) / 2;

    if (this.contentViewChild != null) {
      this.contentViewChild.nativeElement["style"].opacity = 1;
    }
  }

  ngOnDestroy(): void {
    if (this.replayShowSubscription != null) {
      this.replayShowSubscription?.unsubscribe();
      this.replayShowSubscription = null;
    }

    if (this.onShowSubscription != null) {
      this.onShowSubscription?.unsubscribe();
      this.onShowSubscription = null;
    }

    this.removeHandlers();

    super.ngOnDestroy();
  }

  private removeHandlers() {
    try {
      if (this.mouseMoveHandler != null) {
        this.bodyElement.removeEventListener("mouseup", this.mouseUpHandler);
        this.mouseUpHandler = null;
      }

      if (this.mouseMoveHandler != null) {
        this.bodyElement.removeEventListener("mousemove", this.mouseMoveHandler);
        this.mouseMoveHandler = null;
      }
    } catch (e) {
      logger.out.warn(`${this.constructor.name} failed to clean up dialog mouse handlers ${e}`);
    }
  }

  onContainerDestroy() {
    this.removeHandlers();

    super.onContainerDestroy();
  }

  /**
   * _do not modify_ as it will not be incorporated early enough to be useful
   *
   * this is activated before native-desktop embedding.
   */
  onAnimationStart(event: AnimationEvent): void {
    super.onAnimationStart(event);
  }
}
